import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DocumentRenderer from '../DocumentRenderer';
import { brandingService, teacherService, studentService } from '../../services/index.js';
import { API_BASE_URL } from '../../config.js';
import { 
  Palette, Image as ImageIcon, CheckCircle, Download, RefreshCw, Users, User, Settings, Printer, Upload, ChevronDown, Check, Type, Stamp, Layout, Search, Filter, ShieldCheck, Camera, Trash2
} from 'lucide-react';

const getFullAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const formatTeacherName = (tName) => {
  if (!tName) return '';
  const trimmed = String(tName).trim();
  if (!trimmed) return '';
  const clean = trimmed
    .replace(/^tr\.?\s*/i, '')
    .replace(/^(mr|mrs|ms|miss)\.?\s+/i, '')
    .trim();
  return `Tr. ${clean}`;
};


const IDCardManagement = ({
  orgData,
  students = [],
  teachers = [],
  brandingData,
  loadBrandingData,
  onTeacherUpdated,
  onStudentUpdated,
  showSuccessNotification,
  showErrorNotification
}) => {
  // Local synchronized state for immediate photo reflection
  const [localTeachers, setLocalTeachers] = useState(teachers);
  const [localStudents, setLocalStudents] = useState(students);
  const [photoUploading, setPhotoUploading] = useState(false);

  useEffect(() => {
    setLocalTeachers(teachers);
  }, [teachers]);

  useEffect(() => {
    setLocalStudents(students);
  }, [students]);

  // ID Card and Branding Settings State
  const [selectedRoleForCard, setSelectedRoleForCard] = useState('STUDENT');
  const [selectedItemForCard, setSelectedItemForCard] = useState(null);
  const [idGenFilterSearch, setIdGenFilterSearch] = useState('');
  const [idGenFilterCourse, setIdGenFilterCourse] = useState('');
  const [idGenFilterSemester, setIdGenFilterSemester] = useState('');
  const [idGenFilterDept, setIdGenFilterDept] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e3a8a');
  const [secondaryColor, setSecondaryColor] = useState('#f59e0b');
  const [cardFooterText, setCardFooterText] = useState('If found, please return to the administration office.');
  const [academicYearSetting, setAcademicYearSetting] = useState('2026-27');
  const [selectedTemplate, setSelectedTemplate] = useState('Classic Academic');
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [sealFile, setSealFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [signaturePreview, setSignaturePreview] = useState('');
  const [sealPreview, setSealPreview] = useState('');

  // Bulk ID Card Management State
  const [cardMode, setCardMode] = useState('single'); // 'single' or 'bulk'
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkFilterClass, setBulkFilterClass] = useState('');
  const [bulkFilterSemester, setBulkFilterSemester] = useState('');
  const [bulkFilterDept, setBulkFilterDept] = useState('');
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [bulkProgress, setBulkProgress] = useState({ generating: false, current: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  const resetBulkFilters = () => {
    setBulkFilterClass('');
    setBulkFilterSemester('');
    setBulkFilterDept('');
    setBulkSearchTerm('');
  };

  const fetchBranding = async () => {
    try {
      setBrandingLoading(true);
      const res = await brandingService.getBranding();
      if (res && res.success && res.branding) {
        const b = res.branding;
        if (b.primaryColor) setPrimaryColor(b.primaryColor);
        if (b.secondaryColor) setSecondaryColor(b.secondaryColor);
        if (b.cardFooterText) setCardFooterText(b.cardFooterText);
        if (b.academicYear) setAcademicYearSetting(b.academicYear);
        if (b.logoUrl) setLogoPreview(b.logoUrl);
        else if (orgData?.logo || orgData?.logoUrl) setLogoPreview(orgData.logo || orgData.logoUrl);
        if (b.signatureUrl) setSignaturePreview(b.signatureUrl);
        else if (orgData?.signatureUrl) setSignaturePreview(orgData.signatureUrl);
        if (b.sealUrl) setSealPreview(b.sealUrl);
        else if (orgData?.sealUrl) setSealPreview(orgData.sealUrl);
      } else if (orgData) {
        if (orgData.logo || orgData.logoUrl) setLogoPreview(orgData.logo || orgData.logoUrl);
        if (orgData.signatureUrl) setSignaturePreview(orgData.signatureUrl);
        if (orgData.sealUrl) setSealPreview(orgData.sealUrl);
      }
    } catch (err) {
      console.error('Error fetching branding settings for ID Cards:', err);
    } finally {
      setBrandingLoading(false);
    }
  };

  useEffect(() => {
    if (brandingData) {
      setPrimaryColor(brandingData.primaryColor || '#1e3a8a');
      setSecondaryColor(brandingData.secondaryColor || '#f59e0b');
      setCardFooterText(brandingData.cardFooterText || 'If found, please return to the administration office.');
      setAcademicYearSetting(brandingData.academicYear || '2026-27');
      setLogoPreview(brandingData.logoUrl || orgData?.logo || orgData?.logoUrl || '');
      setSignaturePreview(brandingData.signatureUrl || orgData?.signatureUrl || '');
      setSealPreview(brandingData.sealUrl || orgData?.sealUrl || '');
    } else {
      fetchBranding();
    }
  }, [brandingData, orgData]);

  // Asset upload handler
  const handleUploadBrandingAsset = async (type, file) => {
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showErrorNotification('Upload Error', 'Only PNG, JPG, JPEG, and WEBP formats are allowed');
      return;
    }

    try {
      setBrandingLoading(true);
      const res = await brandingService.uploadAsset(type, file);
      if (res && res.success) {
        showSuccessNotification('Upload Successful', `${type} uploaded successfully.`);
        if (type === 'logo') setLogoPreview(res.url);
        if (type === 'signature') setSignaturePreview(res.url);
        if (type === 'seal') setSealPreview(res.url);
        if (typeof loadBrandingData === 'function') {
          await loadBrandingData();
        }
      }
    } catch (error) {
      console.error(error);
      showErrorNotification('Upload Failed', error.message || 'Failed to upload asset');
    } finally {
      setBrandingLoading(false);
    }
  };

  // Save branding settings
  const handleSaveBranding = async (e) => {
    e.preventDefault();
    try {
      setBrandingLoading(true);
      const data = {
        primaryColor,
        secondaryColor,
        cardFooterText,
        academicYear: academicYearSetting,
        logoUrl: logoPreview,
        signatureUrl: signaturePreview,
        sealUrl: sealPreview
      };
      const res = await brandingService.saveBranding(data);
      if (res && res.success) {
        showSuccessNotification('Branding Saved', 'Organization branding configurations saved successfully.');
        if (typeof loadBrandingData === 'function') {
          await loadBrandingData();
        }
      }
    } catch (error) {
      console.error(error);
      showErrorNotification('Save Failed', error.message || 'Failed to save branding settings');
    } finally {
      setBrandingLoading(false);
    }
  };

  // Direct Photo Upload Handler for Staff & Students
  const handleDirectPhotoUpload = async (file) => {
    if (!file || !selectedItemForCard) return;
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showErrorNotification('Invalid File', 'Only PNG, JPG, JPEG, and WEBP formats are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showErrorNotification('File Too Large', 'Photo size must be less than 2MB');
      return;
    }

    try {
      setPhotoUploading(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        try {
          if (selectedRoleForCard === 'TEACHER') {
            const teacherId = selectedItemForCard.teacherId || selectedItemForCard.id;
            const res = await teacherService.updateTeacherPhoto(teacherId, base64Data);
            if (res && (res.success || res.photoUrl)) {
              const updatedPhotoUrl = res.photoUrl || base64Data;
              const updatedItem = {
                ...selectedItemForCard,
                photoUrl: updatedPhotoUrl,
                photo: updatedPhotoUrl
              };
              setSelectedItemForCard(updatedItem);
              setLocalTeachers(prev => prev.map(t => (t.teacherId === teacherId ? { ...t, ...updatedItem } : t)));
              if (typeof onTeacherUpdated === 'function') {
                onTeacherUpdated(updatedItem);
              }
              showSuccessNotification('Photo Uploaded', 'Teacher photo uploaded successfully and reflected on the ID card!');
            } else {
              throw new Error(res?.error || 'Failed to update teacher photo');
            }
          } else {
            const studentId = selectedItemForCard.studentId || selectedItemForCard.id;
            const res = await studentService.updateStudent(selectedItemForCard.id || studentId, {
              ...selectedItemForCard,
              photo: base64Data
            });
            const updatedPhotoUrl = res?.photoUrl || base64Data;
            const updatedItem = {
              ...selectedItemForCard,
              photoUrl: updatedPhotoUrl,
              photo: updatedPhotoUrl
            };
            setSelectedItemForCard(updatedItem);
            setLocalStudents(prev => prev.map(s => (s.studentId === studentId ? { ...s, ...updatedItem } : s)));
            if (typeof onStudentUpdated === 'function') {
              onStudentUpdated(updatedItem);
            }
            showSuccessNotification('Photo Uploaded', 'Student photo uploaded successfully and reflected on the ID card!');
          }
        } catch (err) {
          console.error(err);
          showErrorNotification('Upload Failed', err.message || 'Failed to update photo');
        } finally {
          setPhotoUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      showErrorNotification('Upload Failed', err.message || 'Failed to read file');
      setPhotoUploading(false);
    }
  };

  const handleDirectPhotoRemove = async () => {
    if (!selectedItemForCard) return;
    if (!window.confirm('Are you sure you want to remove this photo?')) return;

    try {
      setPhotoUploading(true);
      if (selectedRoleForCard === 'TEACHER') {
        const teacherId = selectedItemForCard.teacherId || selectedItemForCard.id;
        const res = await teacherService.updateTeacherPhoto(teacherId, 'REMOVE');
        if (res && res.success) {
          const updatedItem = { ...selectedItemForCard, photoUrl: '', photo: '' };
          setSelectedItemForCard(updatedItem);
          setLocalTeachers(prev => prev.map(t => (t.teacherId === teacherId ? { ...t, ...updatedItem } : t)));
          if (typeof onTeacherUpdated === 'function') {
            onTeacherUpdated(updatedItem);
          }
          showSuccessNotification('Photo Removed', 'Teacher photo removed successfully.');
        } else {
          throw new Error(res?.error || 'Failed to remove photo');
        }
      } else {
        const studentId = selectedItemForCard.studentId || selectedItemForCard.id;
        await studentService.updateStudent(selectedItemForCard.id || studentId, {
          ...selectedItemForCard,
          photo: ''
        });
        const updatedItem = { ...selectedItemForCard, photoUrl: '', photo: '' };
        setSelectedItemForCard(updatedItem);
        setLocalStudents(prev => prev.map(s => (s.studentId === studentId ? { ...s, ...updatedItem } : s)));
        if (typeof onStudentUpdated === 'function') {
          onStudentUpdated(updatedItem);
        }
        showSuccessNotification('Photo Removed', 'Student photo removed successfully.');
      }
    } catch (err) {
      console.error(err);
      showErrorNotification('Action Failed', err.message || 'Failed to remove photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Download single ID card as PDF
  // Download single ID card as PDF (format: 'a4' for A4 print sheet, 'cr80' for single card)
  const handleDownloadSinglePDF = async (format = 'a4') => {
    if (!selectedItemForCard) {
      showErrorNotification('Download Error', 'Please select a student or teacher first.');
      return;
    }

    try {
      setLoading(true);

      const frontElement = document.querySelector('#id-card-front-capture .id-card-front') || document.querySelector('.id-card-front') || document.getElementById('id-card-front-capture');
      const backElement = document.querySelector('#id-card-back-capture .id-card-back') || document.querySelector('.id-card-back') || document.getElementById('id-card-back-capture');

      if (!frontElement || !backElement) {
        showErrorNotification('Error', 'Card preview elements not found');
        setLoading(false);
        return;
      }

      const options = {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      };

      const frontCanvas = await html2canvas(frontElement, options);
      const backCanvas = await html2canvas(backElement, options);

      const frontImgData = frontCanvas.toDataURL('image/png');
      const backImgData = backCanvas.toDataURL('image/png');

      const itemName = selectedRoleForCard === 'STUDENT'
        ? (selectedItemForCard.name || 'Student')
        : formatTeacherName(selectedItemForCard.teacherName || selectedItemForCard.name || 'Teacher');
      const itemId = selectedItemForCard.studentId || selectedItemForCard.teacherId || selectedItemForCard.id || 'card';
      const itemClass = selectedItemForCard.className || selectedItemForCard.studentClass || selectedItemForCard.course || '';

      let pdf;

      if (format === 'a4') {
        // Standard A4 sheet (210 x 297 mm) with ONLY Front & Back side-by-side
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        const cardW = 54;
        const cardH = 85.6;
        const gap = 10;
        const totalW = cardW * 2 + gap; // 118mm
        const startX = (210 - totalW) / 2; // 46mm
        const startY = 35; // Top-centered for easy cutting without wasting paper

        // Thin cutting guide outlines around each card
        pdf.setDrawColor(210, 215, 220);
        pdf.setLineDashPattern([2, 2], 0);
        pdf.setLineWidth(0.2);
        pdf.rect(startX - 0.5, startY - 0.5, cardW + 1, cardH + 1);
        pdf.rect(startX + cardW + gap - 0.5, startY - 0.5, cardW + 1, cardH + 1);

        // Add Front Image
        pdf.addImage(frontImgData, 'PNG', startX, startY, cardW, cardH);

        // Add Back Image
        pdf.addImage(backImgData, 'PNG', startX + cardW + gap, startY, cardW, cardH);

      } else {
        // Direct CR-80 PVC Card format (54 x 85.6 mm, 2 pages)
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [54, 85.6]
        });

        pdf.addImage(frontImgData, 'PNG', 0, 0, 54, 85.6);
        pdf.addPage();
        pdf.addImage(backImgData, 'PNG', 0, 0, 54, 85.6);
      }

      // Audit Print Log
      try {
        await brandingService.auditPrint({
          role: selectedRoleForCard,
          id: selectedItemForCard.id,
          reason: format === 'a4' ? 'A4 Print Sheet' : 'PVC Card'
        });
      } catch (auditErr) {
        console.warn('Reprint audit log failed:', auditErr);
      }

      const filename = `${selectedRoleForCard.toLowerCase()}_id_card_${format}_${itemId}.pdf`;
      pdf.save(filename);
      showSuccessNotification('PDF Downloaded', format === 'a4' ? 'A4 Printable Sheet downloaded with Front & Back side-by-side.' : 'PVC Card PDF downloaded.');
    } catch (err) {
      console.error(err);
      showErrorNotification('Download Failed', 'Failed to generate ID Card PDF');
    } finally {
      setLoading(false);
    }
  };

  // Bulk ID Card functions
  const getBulkFilteredList = () => {
    const list = selectedRoleForCard === 'STUDENT' ? localStudents : localTeachers;
    return list.filter(item => {
      const itemName = selectedRoleForCard === 'STUDENT' ? (item.name || '') : (item.teacherName || item.name || '');
      const matchSearch = !bulkSearchTerm || itemName.toLowerCase().includes(bulkSearchTerm.toLowerCase()) || (item.studentId || item.teacherId || '').toLowerCase().includes(bulkSearchTerm.toLowerCase());

      let matchClassOrDegree = true;
      let matchSemester = true;

      const isSchool = orgData?.institutionType?.toUpperCase() === 'SCHOOL';

      if (selectedRoleForCard === 'STUDENT') {
        if (isSchool) {
          matchClassOrDegree = !bulkFilterClass || (item.className || item.studentClass || '') === bulkFilterClass;
        } else {
          const studentDegree = item.course || item.degree || item.courseName || item.className || item.studentClass || '';
          matchClassOrDegree = !bulkFilterClass || studentDegree === bulkFilterClass;
          matchSemester = !bulkFilterSemester || String(item.semester || '') === String(bulkFilterSemester);
        }
      }

      const matchDept = !bulkFilterDept || (item.department || '') === bulkFilterDept;
      return matchSearch && matchClassOrDegree && matchSemester && matchDept;
    });
  };

  const toggleBulkSelect = (id) => {
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const filtered = getBulkFilteredList();
    const allIds = filtered.map(item => selectedRoleForCard === 'STUDENT' ? item.studentId : item.teacherId);
    const allSelected = allIds.every(id => bulkSelectedIds.has(id));
    if (allSelected) {
      setBulkSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setBulkSelectedIds(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const getCardDataForItem = (item) => {
    if (!item) return {};
    const isStudent = selectedRoleForCard === 'STUDENT';
    const isSchool = orgData?.institutionType?.toUpperCase() === 'SCHOOL' || !['DEGREE_COLLEGE','PUC_COLLEGE','PU_COLLEGE','ENGINEERING_COLLEGE','MED_COLLEGE'].includes(orgData?.institutionType);
    const rawTeacherName = item.teacherName || item.name || '';
    const formattedName = isStudent ? (item.name || item.studentName || '') : formatTeacherName(rawTeacherName);

    return {
      name: formattedName,
      photoUrl: item.photoUrl || item.photo || item.studentPhoto || item.avatarUrl || item.avatar || item.profilePhoto || item.imageUrl || item.image || '',
      id: isStudent ? (item.rollNo || item.rollNumber || item.studentId || item.regNo || item.admissionNo || '') : (item.teacherId || item.employeeId || ''),
      role: selectedRoleForCard,
      department: item.department || item.departmentName || '',
      designation: isStudent ? 'Student' : (item.designation || item.role || 'Teacher'),
      validUntil: item.validUntil || '',
      emergencyContact: item.emergencyContactPhone || item.emergencyContact || item.parentPhone || item.guardianPhone || item.phone || item.phoneNumber || '',
      address: item.address || item.city || '',
      verificationToken: item.verificationToken || item.studentId || item.teacherId || '',
      fatherName: item.fatherName || item.guardianName || item.parentName || '',
      motherName: item.motherName || '',
      bloodGroup: item.bloodGroup || '',
      dob: item.dob || item.dateOfBirth || '',
      phone: item.phone || item.phoneNumber || item.fatherPhone || item.motherPhone || '',
      course: item.course || item.degree || item.courseName || '',
      className: item.className || item.studentClass || '',
      section: item.section || '',
      trustName: brandingData?.trustName || orgData?.trustName || localStorage.getItem('trustName') || '',
      orgPhone: orgData?.phoneNumber || orgData?.phone || orgData?.contactPhone || orgData?.adminPhone || localStorage.getItem('adminPhone') || localStorage.getItem('phoneNumber') || localStorage.getItem('phone') || '',
      adminPhone: orgData?.phoneNumber || orgData?.phone || orgData?.contactPhone || orgData?.adminPhone || localStorage.getItem('adminPhone') || localStorage.getItem('phoneNumber') || localStorage.getItem('phone') || '',
      orgEmail: orgData?.email || orgData?.contactEmail || localStorage.getItem('email') || '',
      isSchool
    };
  };

  const handleBulkDownloadPDF = async () => {
    if (bulkSelectedIds.size === 0) {
      showErrorNotification('No Selection', 'Please select at least one member for bulk generation.');
      return;
    }

    const list = selectedRoleForCard === 'STUDENT' ? localStudents : localTeachers;
    const selectedItems = list.filter(item => {
      const itemId = selectedRoleForCard === 'STUDENT' ? item.studentId : item.teacherId;
      return bulkSelectedIds.has(itemId);
    });

    setBulkProgress({ generating: true, current: 0, total: selectedItems.length * 2 });

    try {
      // Create A4 PDF
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = 210;
      const pageH = 297;
      const cardW = 54;
      const cardH = 85.6;
      const cols = 3;
      const rows = 3;
      const marginX = (pageW - cols * cardW) / (cols + 1);
      const marginY = (pageH - rows * cardH) / (rows + 1);

      // Hidden container for rendering in visible coordinate space (transparent) so images decode properly
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:0;left:0;opacity:0.01;pointer-events:none;z-index:-9999;width:500px;background:#ffffff;';
      document.body.appendChild(container);

      const canvasOptions = { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false };
      const { createRoot } = await import('react-dom/client');

      // Resolved active branding assets
      const activeLogo = logoPreview || brandingData?.logoUrl || brandingData?.logo || orgData?.logo || orgData?.logoUrl || '';
      const activeSig = signaturePreview || brandingData?.signatureUrl || brandingData?.signature || orgData?.signatureUrl || '';
      const activeSeal = sealPreview || brandingData?.sealUrl || brandingData?.seal || orgData?.sealUrl || '';

      // Helper to wait for all images inside a wrapper element to fully load
      const waitForAllImages = (element) => {
        const imgs = Array.from(element.querySelectorAll('img'));
        return Promise.all(
          imgs.map(img => {
            if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
            return new Promise(res => {
              img.onload = res;
              img.onerror = res;
              setTimeout(res, 2500); // 2.5s safety timeout
            });
          })
        );
      };

      // Chunk items into groups of 9
      const chunks = [];
      for (let i = 0; i < selectedItems.length; i += 9) {
        chunks.push(selectedItems.slice(i, i + 9));
      }

      let progressCount = 0;
      for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
        const chunk = chunks[chunkIdx];

        if (chunkIdx > 0) {
          pdf.addPage();
        }

        // 1. Generate Fronts
        for (let idx = 0; idx < chunk.length; idx++) {
          const item = chunk[idx];
          const currentProgress = ++progressCount;
          setBulkProgress(prev => ({ ...prev, current: currentProgress }));

          const wrapper = document.createElement('div');
          container.appendChild(wrapper);
          const root = createRoot(wrapper);
          const cardData = getCardDataForItem(item);

          await new Promise(resolve => {
            root.render(
              React.createElement(DocumentRenderer, {
                documentType: 'ID_CARD',
                templateName: selectedTemplate,
                primaryColor: primaryColor,
                secondaryColor: secondaryColor,
                logoUrl: activeLogo,
                signatureUrl: activeSig,
                sealUrl: activeSeal,
                institutionName: orgData?.orgName || orgData?.institutionName || orgData?.name || orgData?.collegeName || 'Institution Name',
                institutionAddress: orgData?.address || '',
                footerText: cardFooterText,
                academicYear: academicYearSetting,
                side: 'front',
                data: cardData
              })
            );
            setTimeout(resolve, 100);
          });

          // Wait until photo, logo, QR code, etc. have fully loaded into the DOM
          await waitForAllImages(wrapper);
          await new Promise(r => setTimeout(r, 100));

          const frontEl = wrapper.querySelector('.id-card-front');
          if (frontEl) {
            const frontCanvas = await html2canvas(frontEl, canvasOptions);
            const frontImg = frontCanvas.toDataURL('image/png');

            const col = idx % cols;
            const row = Math.floor(idx / cols);
            const x = marginX + col * (cardW + marginX);
            const y = marginY + row * (cardH + marginY);
            pdf.addImage(frontImg, 'PNG', x, y, cardW, cardH);
          }

          root.unmount();
          container.removeChild(wrapper);
        }

        // 2. Add Backs Page
        pdf.addPage();

        // 3. Generate Backs
        for (let idx = 0; idx < chunk.length; idx++) {
          const item = chunk[idx];
          const currentProgress = ++progressCount;
          setBulkProgress(prev => ({ ...prev, current: currentProgress }));

          const wrapper = document.createElement('div');
          container.appendChild(wrapper);
          const root = createRoot(wrapper);
          const cardData = getCardDataForItem(item);

          await new Promise(resolve => {
            root.render(
              React.createElement(DocumentRenderer, {
                documentType: 'ID_CARD',
                templateName: selectedTemplate,
                primaryColor: primaryColor,
                secondaryColor: secondaryColor,
                logoUrl: activeLogo,
                signatureUrl: activeSig,
                sealUrl: activeSeal,
                institutionName: orgData?.orgName || orgData?.institutionName || orgData?.name || orgData?.collegeName || 'Institution Name',
                institutionAddress: orgData?.address || '',
                footerText: cardFooterText,
                academicYear: academicYearSetting,
                side: 'back',
                data: cardData
              })
            );
            setTimeout(resolve, 100);
          });

          // Wait until barcode, seal, signature have fully loaded
          await waitForAllImages(wrapper);
          await new Promise(r => setTimeout(r, 100));

          const backEl = wrapper.querySelector('.id-card-back');
          if (backEl) {
            const backCanvas = await html2canvas(backEl, canvasOptions);
            const backImg = backCanvas.toDataURL('image/png');

            // Mirror column for duplex alignment: col 0 -> col 2, col 1 -> col 1, col 2 -> col 0
            const col = (cols - 1) - (idx % cols);
            const row = Math.floor(idx / cols);
            const x = marginX + col * (cardW + marginX);
            const y = marginY + row * (cardH + marginY);
            pdf.addImage(backImg, 'PNG', x, y, cardW, cardH);
          }

          root.unmount();
          container.removeChild(wrapper);
        }
      }

      document.body.removeChild(container);

      const filename = `bulk_id_cards_${selectedRoleForCard.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      showSuccessNotification('Bulk PDF Generated', `${selectedItems.length} ID cards (front & back) have been generated successfully.`);
    } catch (err) {
      console.error('Bulk PDF generation error:', err);
      showErrorNotification('Generation Failed', 'Failed to generate bulk ID card PDF. ' + (err.message || ''));
    } finally {
      setBulkProgress({ generating: false, current: 0, total: 0 });
    }
  };


  const getFilteredSingleList = () => {
    const list = selectedRoleForCard === 'STUDENT' ? localStudents : localTeachers;
    return list.filter(item => {
      const rawName = selectedRoleForCard === 'STUDENT' ? (item.name || '') : (item.teacherName || item.name || '');
      const formattedName = selectedRoleForCard === 'STUDENT' ? rawName : formatTeacherName(rawName);
      const id = (item.studentId || item.teacherId || '');
      const search = (idGenFilterSearch || '').toLowerCase();
      return !search || rawName.toLowerCase().includes(search) || formattedName.toLowerCase().includes(search) || id.toLowerCase().includes(search);
    });
  };

  const handleSelectAllBulk = () => {
    const filtered = getBulkFilteredList();
    if (bulkSelectedIds.size === filtered.length && filtered.length > 0) {
      setBulkSelectedIds(new Set());
    } else {
      const newSet = new Set();
      filtered.forEach(item => {
        const id = item.studentId || item.teacherId || item.id;
        if (id) newSet.add(id);
      });
      setBulkSelectedIds(newSet);
    }
  };

  return (

    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '16px', boxSizing: 'border-box', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ width: '100%', margin: '0 auto' }}>
        
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#ffffff', padding: '12px 20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Layout size={20} color="#0ea5e9" />
              ID Card Studio
            </h2>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '13px' }}>Design, configure, and generate premium identity cards for your institution.</p>
          </div>
          <div>
            <button 
              onClick={handleSaveBranding}
              disabled={brandingLoading}
              style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: brandingLoading ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)',
                transition: 'all 0.2s ease',
                opacity: brandingLoading ? 0.8 : 1
              }}
              onMouseOver={(e) => { if(!brandingLoading) e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.3)'; }}
              onMouseOut={(e) => { if(!brandingLoading) e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.2)'; }}
            >
              {brandingLoading ? <RefreshCw className="spin" size={18} /> : <CheckCircle size={18} />} 
              {brandingLoading ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>

        {/* 3-Column Layout */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'start' }}>
          
          {/* ======================================= */}
          {/* LEFT COLUMN: BRANDING & CONFIGURATION */}
          {/* ======================================= */}
          <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <Palette size={18} color="#6366f1" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Brand Theme</h3>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '8px' }}>Primary Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px 8px', background: '#f8fafc' }}>
                    <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width: '28px', height: '28px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', background: 'transparent' }} />
                    <span style={{ fontSize: '13px', color: '#334155', fontFamily: 'monospace' }}>{primaryColor.toUpperCase()}</span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '8px' }}>Secondary Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px 8px', background: '#f8fafc' }}>
                    <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} style={{ width: '28px', height: '28px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', background: 'transparent' }} />
                    <span style={{ fontSize: '13px', color: '#334155', fontFamily: 'monospace' }}>{secondaryColor.toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '8px' }}>Academic Year</label>
                <div style={{ position: 'relative' }}>
                  <select 
                    value={academicYearSetting} 
                    onChange={e => setAcademicYearSetting(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#f8fafc', appearance: 'none', color: '#334155' }}
                  >
                    <option value="2025-26">2025-26</option>
                    <option value="2026-27">2026-27</option>
                    <option value="2027-28">2027-28</option>
                  </select>
                  <ChevronDown size={16} color="#94a3b8" style={{ position: 'absolute', right: '12px', top: '12px', pointerEvents: 'none' }} />
                </div>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '8px' }}>Footer / Emergency Info</label>
                <textarea 
                  value={cardFooterText}
                  onChange={e => setCardFooterText(e.target.value)}
                  placeholder="e.g. If found, please return to the school administration."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc', minHeight: '60px', resize: 'vertical', color: '#334155', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImageIcon size={18} color="#f59e0b" />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Visual Assets</h3>
                </div>
                {(logoPreview || signaturePreview || sealPreview) && (
                  <span style={{ fontSize: '11px', background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '2px 8px', borderRadius: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Check size={12} /> Synced from Settings
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Logo Upload / Display */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Institution Logo</label>
                    {logoPreview ? (
                      <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Check size={12}/> Connected
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>Not Set</span>
                    )}
                  </div>
                  
                  {logoPreview ? (
                    <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '2px' }}>
                          <img src={getFullAssetUrl(logoPreview)} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Active Logo</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Used on card header</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input type="file" id="logoUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('logo', e.target.files[0])} />
                        <label htmlFor="logoUpload" className="btn btn-xs btn-outline-primary" style={{ cursor: 'pointer', margin: 0, padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Replace
                        </label>
                        <button type="button" className="btn btn-xs btn-outline-danger" onClick={() => setLogoPreview('')} style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', textAlign: 'center', background: '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e=>e.currentTarget.style.background='#f1f5f9'} onMouseOut={e=>e.currentTarget.style.background='#f8fafc'}>
                      <input type="file" id="logoUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('logo', e.target.files[0])} />
                      <label htmlFor="logoUpload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Upload size={22} color="#94a3b8" />
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Click to upload logo (PNG/JPG)</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Principal Signature Upload / Display */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Principal Signature</label>
                    {signaturePreview ? (
                      <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Check size={12}/> Connected
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>Not Set</span>
                    )}
                  </div>

                  {signaturePreview ? (
                    <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '64px', height: '36px', borderRadius: '6px', background: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '2px' }}>
                          <img src={getFullAssetUrl(signaturePreview)} alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Active Signature</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Used for authorization</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input type="file" id="signatureUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('signature', e.target.files[0])} />
                        <label htmlFor="signatureUpload" className="btn btn-xs btn-outline-primary" style={{ cursor: 'pointer', margin: 0, padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Replace
                        </label>
                        <button type="button" className="btn btn-xs btn-outline-danger" onClick={() => setSignaturePreview('')} style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', textAlign: 'center', background: '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e=>e.currentTarget.style.background='#f1f5f9'} onMouseOut={e=>e.currentTarget.style.background='#f8fafc'}>
                      <input type="file" id="signatureUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('signature', e.target.files[0])} />
                      <label htmlFor="signatureUpload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Type size={22} color="#94a3b8" />
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Click to upload signature (Transparent PNG)</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Official Seal Upload / Display */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Institution Seal / Stamp</label>
                    {sealPreview ? (
                      <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Check size={12}/> Connected
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>Not Set</span>
                    )}
                  </div>

                  {sealPreview ? (
                    <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '2px' }}>
                          <img src={getFullAssetUrl(sealPreview)} alt="Seal" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Active Stamp/Seal</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Used on card reverse</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input type="file" id="sealUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('seal', e.target.files[0])} />
                        <label htmlFor="sealUpload" className="btn btn-xs btn-outline-primary" style={{ cursor: 'pointer', margin: 0, padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Replace
                        </label>
                        <button type="button" className="btn btn-xs btn-outline-danger" onClick={() => setSealPreview('')} style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '6px', fontWeight: '600' }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', textAlign: 'center', background: '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e=>e.currentTarget.style.background='#f1f5f9'} onMouseOut={e=>e.currentTarget.style.background='#f8fafc'}>
                      <input type="file" id="sealUpload" hidden accept="image/png, image/jpeg, image/webp" onChange={(e) => handleUploadBrandingAsset('seal', e.target.files[0])} />
                      <label htmlFor="sealUpload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Stamp size={22} color="#94a3b8" />
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Click to upload seal (Transparent PNG)</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


          {/* ======================================= */}
          {/* CENTER COLUMN: LIVE PREVIEW & TEMPLATES */}
          {/* ======================================= */}
          <div style={{ flex: '2 1 400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Template Selector */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', display: 'flex', gap: '8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {['Classic Academic', 'Modern Corporate', 'Minimalist', 'Creative'].map(template => (
                <button
                  key={template}
                  onClick={() => setSelectedTemplate(template)}
                  style={{
                    flex: '1 0 auto',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: selectedTemplate === template ? '#eff6ff' : 'transparent',
                    color: selectedTemplate === template ? '#2563eb' : '#64748b',
                    fontWeight: selectedTemplate === template ? '600' : '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: selectedTemplate === template ? '0 2px 8px rgba(37, 99, 235, 0.1)' : 'none'
                  }}
                  onMouseOver={(e) => { if(selectedTemplate !== template) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseOut={(e) => { if(selectedTemplate !== template) e.currentTarget.style.background = 'transparent'; }}
                >
                  {template}
                </button>
              ))}
            </div>

            {/* Live Preview Pane */}
            <div style={{ 
              background: '#ffffff', 
              borderRadius: '16px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: '600px'
            }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck size={18} color="#10b981" /> Live Card Preview
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontWeight: '500' }}>Updates Instantly</span>
              </div>
              
              <div style={{ flex: 1, background: '#f8fafc', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px 20px', gap: '30px' }}>
                
                {!selectedItemForCard && (
                  <div style={{
                    background: '#eff6ff',
                    color: '#1e40af',
                    border: '1px solid #bfdbfe',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    fontSize: '12.5px',
                    fontWeight: '600',
                    textAlign: 'center',
                    maxWidth: '360px',
                    zIndex: 5,
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.08)'
                  }}>
                    👈 Select a student or teacher from the right panel to preview their live database information
                  </div>
                )}

                {/* ID Card Front Preview */}
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-24px', left: '50%', transform: 'translateX(-50%)', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Front</div>
                  <div style={{ boxShadow: '0 15px 35px rgba(0,0,0,0.1), 0 5px 15px rgba(0,0,0,0.05)', borderRadius: '12px', overflow: 'hidden' }}>
                    <div id="id-card-front-capture" style={{ background: '#fff' }}>
                      <DocumentRenderer
                        documentType="ID_CARD"
                        templateName={selectedTemplate}
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        logoUrl={logoPreview || orgData?.logo || orgData?.logoUrl}
                        signatureUrl={signaturePreview || orgData?.signatureUrl}
                        sealUrl={sealPreview || orgData?.sealUrl}
                        institutionName={orgData?.orgName || orgData?.institutionName || orgData?.name || orgData?.collegeName || 'Institution Name'}
                        institutionAddress={orgData?.address || ''}
                        footerText={cardFooterText}
                        academicYear={academicYearSetting}
                        side="front"
                        data={getCardDataForItem(selectedItemForCard)}
                      />
                    </div>
                  </div>
                </div>

                {/* ID Card Back Preview */}
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-24px', left: '50%', transform: 'translateX(-50%)', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Back</div>
                  <div style={{ boxShadow: '0 15px 35px rgba(0,0,0,0.1), 0 5px 15px rgba(0,0,0,0.05)', borderRadius: '12px', overflow: 'hidden' }}>
                    <div id="id-card-back-capture" style={{ background: '#fff' }}>
                      <DocumentRenderer
                        documentType="ID_CARD"
                        templateName={selectedTemplate}
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        logoUrl={logoPreview || orgData?.logo || orgData?.logoUrl}
                        signatureUrl={signaturePreview || orgData?.signatureUrl}
                        sealUrl={sealPreview || orgData?.sealUrl}
                        institutionName={orgData?.orgName || orgData?.institutionName || orgData?.name || orgData?.collegeName || 'Institution Name'}
                        institutionAddress={orgData?.address || ''}
                        footerText={cardFooterText}
                        academicYear={academicYearSetting}
                        side="back"
                        data={getCardDataForItem(selectedItemForCard)}
                      />
                    </div>
                  </div>
                </div>

                {/* Instant Photo Upload Banner for Selected Member */}
                {selectedItemForCard && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '10px 18px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                    zIndex: 5
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      overflow: 'hidden', background: '#f1f5f9',
                      border: '1px solid #cbd5e1', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      {(selectedItemForCard.photoUrl || selectedItemForCard.photo) ? (
                        <img src={getFullAssetUrl(selectedItemForCard.photoUrl || selectedItemForCard.photo)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '14px' }}>📷</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#1e293b' }}>
                        {selectedRoleForCard === 'STUDENT' ? 'Student Photo' : 'Teacher Photo'}: {(selectedItemForCard.photoUrl || selectedItemForCard.photo) ? 'Uploaded' : 'Missing'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {selectedRoleForCard === 'STUDENT' ? (selectedItemForCard.name || '') : formatTeacherName(selectedItemForCard.teacherName || selectedItemForCard.name || '')}
                      </span>
                    </div>
                    <label style={{
                      background: '#2563eb',
                      color: '#ffffff',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: photoUploading ? 'wait' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 6px rgba(37,99,235,0.25)',
                      marginLeft: '6px'
                    }}>
                      {photoUploading ? <RefreshCw size={13} className="spin" /> : <Camera size={13} />}
                      <span>{photoUploading ? 'Uploading...' : ((selectedItemForCard.photoUrl || selectedItemForCard.photo) ? 'Change Photo' : 'Upload Photo')}</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        style={{ display: 'none' }}
                        disabled={photoUploading}
                        onChange={(e) => handleDirectPhotoUpload(e.target.files[0])}
                      />
                    </label>
                    {(selectedItemForCard.photoUrl || selectedItemForCard.photo) && (
                      <button
                        type="button"
                        onClick={handleDirectPhotoRemove}
                        disabled={photoUploading}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        title="Remove photo"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                )}
                
                {/* Decorative Elements */}
                <div style={{ position: 'absolute', width: '300px', height: '300px', background: primaryColor, opacity: 0.03, borderRadius: '50%', filter: 'blur(40px)', top: '-100px', left: '-100px', pointerEvents: 'none' }}></div>
                <div style={{ position: 'absolute', width: '200px', height: '200px', background: secondaryColor, opacity: 0.03, borderRadius: '50%', filter: 'blur(30px)', bottom: '-50px', right: '-50px', pointerEvents: 'none' }}></div>
              </div>
            </div>
          </div>


          {/* ======================================= */}
          {/* RIGHT COLUMN: GENERATION & WIZARD */}
          {/* ======================================= */}
          <div style={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Mode Switcher */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '6px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', display: 'flex' }}>
              <button
                onClick={() => setCardMode('single')}
                style={{
                  flex: 1, padding: '10px', borderRadius: '12px', border: 'none',
                  background: cardMode === 'single' ? '#f1f5f9' : 'transparent',
                  color: cardMode === 'single' ? '#0f172a' : '#64748b',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <User size={16} /> Single
              </button>
              <button
                onClick={() => setCardMode('bulk')}
                style={{
                  flex: 1, padding: '10px', borderRadius: '12px', border: 'none',
                  background: cardMode === 'bulk' ? '#f1f5f9' : 'transparent',
                  color: cardMode === 'bulk' ? '#0f172a' : '#64748b',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <Users size={16} /> Bulk Wizard
              </button>
            </div>

            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', minHeight: '550px', display: 'flex', flexDirection: 'column' }}>
              
              {/* Common Role Selector */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target Audience</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['STUDENT', 'TEACHER'].map(role => (
                    <button
                      key={role}
                      onClick={() => { setSelectedRoleForCard(role); setSelectedItemForCard(null); setBulkSelectedIds(new Set()); }}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '10px',
                        border: selectedRoleForCard === role ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        background: selectedRoleForCard === role ? '#eff6ff' : '#fff',
                        color: selectedRoleForCard === role ? '#2563eb' : '#475569',
                        fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s'
                      }}
                    >
                      {role === 'STUDENT' ? 'Students' : 'Teachers'}
                    </button>
                  ))}
                </div>
              </div>

              {cardMode === 'single' ? (
                /* SINGLE GENERATION */
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  
                  {/* Search Input */}
                  <div style={{ position: 'relative', marginBottom: '20px' }}>
                    <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                    <input
                      type="text"
                      placeholder={`Search ${selectedRoleForCard === 'STUDENT' ? 'student' : 'teacher'} by name...`}
                      value={idGenFilterSearch}
                      onChange={e => setIdGenFilterSearch(e.target.value)}
                      style={{ width: '100%', padding: '12px 12px 12px 36px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', background: '#f8fafc', color: '#334155', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Results List */}
                  <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '10px', overflowY: 'auto', maxHeight: '300px', marginBottom: '20px', background: '#f8fafc' }}>
                    {(typeof getFilteredSingleList === 'function' ? getFilteredSingleList() : []).length > 0 ? (
                      getFilteredSingleList().slice(0, 50).map(item => {
                        const name = selectedRoleForCard === 'STUDENT' ? item.name : formatTeacherName(item.teacherName || item.name);
                        const displayId = item.studentId || item.teacherId;
                        const isSelected = selectedItemForCard && ((selectedItemForCard.studentId === item.studentId) || (selectedItemForCard.teacherId === item.teacherId));
                        
                        return (
                          <div
                            key={displayId || Math.random()}
                            onClick={() => setSelectedItemForCard(item)}
                            style={{
                              padding: '10px 14px',
                              borderBottom: '1px solid #e2e8f0',
                              background: isSelected ? '#eff6ff' : '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={e => { if(!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseOut={e => { if(!isSelected) e.currentTarget.style.background = '#fff'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{
                                width: '34px',
                                height: '34px',
                                borderRadius: '50%',
                                background: '#e2e8f0',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                border: (item.photoUrl || item.photo) ? '2px solid #3b82f6' : '1px solid #cbd5e1'
                              }}>
                                {(item.photoUrl || item.photo) ? (
                                  <img src={getFullAssetUrl(item.photoUrl || item.photo)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <span style={{ fontSize: '15px' }}>{selectedRoleForCard === 'STUDENT' ? '🎓' : '👨‍🏫'}</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '13.5px', fontWeight: '600', color: isSelected ? '#1e40af' : '#1e293b' }}>{name || 'Unknown'}</span>
                                <span style={{ fontSize: '11.5px', color: '#64748b' }}>{displayId || 'No ID'}</span>
                              </div>
                            </div>
                            {isSelected && <CheckCircle size={16} color="#2563eb" />}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                        No results found
                      </div>
                    )}
                  </div>

                  {/* Photo Management Card for Selected Member */}
                  {selectedItemForCard && (
                    <div style={{
                      background: '#f8fafc',
                      border: '1.5px dashed #cbd5e1',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #cbd5e1',
                        flexShrink: 0
                      }}>
                        {(selectedItemForCard.photoUrl || selectedItemForCard.photo) ? (
                          <img src={getFullAssetUrl(selectedItemForCard.photoUrl || selectedItemForCard.photo)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '20px' }}>📷</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedRoleForCard === 'STUDENT' ? 'Student Photo' : 'Teacher Photo'}
                        </div>
                        <div style={{ fontSize: '11px', color: (selectedItemForCard.photoUrl || selectedItemForCard.photo) ? '#16a34a' : '#ea580c', fontWeight: '600' }}>
                          {(selectedItemForCard.photoUrl || selectedItemForCard.photo) ? '✓ Photo Attached' : '⚠️ Photo Missing'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <label style={{
                          background: '#2563eb',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: photoUploading ? 'wait' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 6px rgba(37,99,235,0.25)',
                          transition: 'all 0.15s'
                        }}>
                          {photoUploading ? <RefreshCw size={13} className="spin" /> : <Upload size={13} />}
                          <span>{photoUploading ? 'Saving...' : ((selectedItemForCard.photoUrl || selectedItemForCard.photo) ? 'Change' : 'Upload')}</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            style={{ display: 'none' }}
                            disabled={photoUploading}
                            onChange={(e) => handleDirectPhotoUpload(e.target.files[0])}
                          />
                        </label>
                        {(selectedItemForCard.photoUrl || selectedItemForCard.photo) && (
                          <button
                            type="button"
                            onClick={handleDirectPhotoRemove}
                            disabled={photoUploading}
                            style={{
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              padding: '6px 9px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="Remove photo"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Generate Buttons */}
                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      onClick={() => handleDownloadSinglePDF('a4')}
                      disabled={!selectedItemForCard || loading}
                      style={{
                        width: '100%',
                        padding: '13px',
                        background: (!selectedItemForCard || loading) ? '#e2e8f0' : 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                        color: (!selectedItemForCard || loading) ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: (!selectedItemForCard || loading) ? 'not-allowed' : 'pointer',
                        boxShadow: (!selectedItemForCard || loading) ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.3)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { if(selectedItemForCard && !loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseOut={(e) => { if(selectedItemForCard && !loading) e.currentTarget.style.transform = 'none'; }}
                    >
                      {loading ? <RefreshCw className="spin" size={18} /> : <Printer size={18} />}
                      {loading ? 'Generating PDF...' : 'Download A4 Print Sheet (Front & Back)'}
                    </button>

                    <button
                      onClick={() => handleDownloadSinglePDF('cr80')}
                      disabled={!selectedItemForCard || loading}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#ffffff',
                        color: (!selectedItemForCard || loading) ? '#94a3b8' : '#2563eb',
                        border: (!selectedItemForCard || loading) ? '1px solid #e2e8f0' : '1.5px solid #2563eb',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: (!selectedItemForCard || loading) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Download size={15} />
                      Download Single PVC Card (CR-80)
                    </button>
                    {!selectedItemForCard && <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '4px', marginBottom: 0 }}>Select a user from the list to preview and generate.</p>}
                  </div>
                </div>
              ) : (
                /* BULK GENERATION WIZARD */
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  
                  {/* Filters */}
                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                      <Filter size={14} color="#64748b" />
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Target Filters</span>
                    </div>

                    {selectedRoleForCard === 'STUDENT' && (
                      <div style={{ marginBottom: '12px' }}>
                        <select
                          value={bulkFilterClass}
                          onChange={e => setBulkFilterClass(e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', appearance: 'none', background: '#fff' }}
                        >
                          <option value="">All Classes / Degrees</option>
                          {Array.from(new Set(students.map(s => (orgData?.institutionType?.toUpperCase() === 'SCHOOL' ? (s.className || s.studentClass) : (s.course || s.degree || s.courseName || s.className || s.studentClass))))).filter(Boolean).sort().map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div style={{ position: 'relative' }}>
                      <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '10px' }} />
                      <input
                        type="text"
                        placeholder="Search specifically..."
                        value={bulkSearchTerm}
                        onChange={e => setBulkSearchTerm(e.target.value)}
                        style={{ width: '100%', padding: '10px 10px 10px 32px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Summary / Action */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px', textAlign: 'center', background: '#f8fafc', marginBottom: '20px' }}>
                    <div style={{ background: '#eff6ff', width: '56px', height: '56px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                      <Printer size={28} color="#2563eb" />
                    </div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                      {(typeof getBulkFilteredList === 'function' ? getBulkFilteredList() : []).length} Cards Ready
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                      Based on your current filters, {(typeof getBulkFilteredList === 'function' ? getBulkFilteredList() : []).length} {selectedRoleForCard.toLowerCase()} ID cards will be generated into a printable A4 grid (3x3).
                    </p>
                    
                    <button
                      onClick={handleSelectAllBulk}
                      style={{ marginTop: '16px', background: '#ffffff', border: '1px solid #cbd5e1', color: '#334155', padding: '8px 16px', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      {bulkSelectedIds.size === (typeof getBulkFilteredList === 'function' ? getBulkFilteredList().length : 0) && bulkSelectedIds.size > 0 ? 'Deselect All' : 'Select All Filtered'}
                    </button>
                  </div>

                  {/* Bulk Generate Button */}
                  <div style={{ marginTop: 'auto' }}>
                    <button
                      onClick={handleBulkDownloadPDF}
                      disabled={bulkSelectedIds.size === 0 || bulkProgress.generating}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: (bulkSelectedIds.size === 0 || bulkProgress.generating) ? '#e2e8f0' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        color: (bulkSelectedIds.size === 0 || bulkProgress.generating) ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: (bulkSelectedIds.size === 0 || bulkProgress.generating) ? 'not-allowed' : 'pointer',
                        boxShadow: (bulkSelectedIds.size === 0 || bulkProgress.generating) ? 'none' : '0 4px 14px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { if(bulkSelectedIds.size > 0 && !bulkProgress.generating) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseOut={(e) => { if(bulkSelectedIds.size > 0 && !bulkProgress.generating) e.currentTarget.style.transform = 'none'; }}
                    >
                      {bulkProgress.generating ? <RefreshCw className="spin" size={18} /> : <Printer size={18} />}
                      {bulkProgress.generating 
                        ? `Generating ${bulkProgress.current}/${bulkProgress.total}...`
                        : `Generate ${bulkSelectedIds.size} Cards (A4 PDF)`
                      }
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '10px', marginBottom: 0 }}>
                      Cards are arranged 9 per page for standard A4 printing.
                    </p>
                  </div>

                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default IDCardManagement;
